/**
 * Website Hero + optional splash gate (Settings → Hero).
 * Lives in storefront.moduleSettings.siteHero (exported with storefront JSON).
 * Designed to grow: more wordmarks, scenes, CTAs later.
 */

/** Splash wordmark options — EasyLight SVG is tenant-custom (Settings → Кастом тенанта). */
export const SITE_HERO_WORDMARK_PRESETS = ['easylight', 'text', 'none'] as const;

export type SiteHeroWordmarkPreset = (typeof SITE_HERO_WORDMARK_PRESETS)[number];

/** Generic Hero tab radios — no tenant-specific SVG marks. */
export const SITE_HERO_WORDMARK_PRESETS_GENERIC = ['text', 'none'] as const;

/** Still / slideshow / mosaic of catalog photos. */
export const SITE_HERO_MEDIA_MODES = ['single', 'slideshow', 'mosaic'] as const;
export type SiteHeroMediaMode = (typeof SITE_HERO_MEDIA_MODES)[number];

/** Typical hero carousel depth — enough variety without looking busy. */
export const SITE_HERO_SLIDES_MAX = 6;

/**
 * Mosaic pool cap = densest preset (5×4). Slideshow UI still caps at SITE_HERO_SLIDES_MAX.
 */
export const SITE_HERO_MOSAIC_SLIDES_MAX = 20;

/** Absolute storage cap for heroSlideUrls (mosaic needs the larger pool). */
export const SITE_HERO_URLS_STORAGE_MAX = SITE_HERO_MOSAIC_SLIDES_MAX;

/** Desktop mosaic layouts (cols × rows). Default 5×3. */
export const SITE_HERO_MOSAIC_GRIDS = ['5x3', '6x3', '4x3', '5x4'] as const;
export type SiteHeroMosaicGrid = (typeof SITE_HERO_MOSAIC_GRIDS)[number];

export const SITE_HERO_OVERLAY_FADE_COLORS = ['black', 'white'] as const;
export type SiteHeroOverlayFadeColor = (typeof SITE_HERO_OVERLAY_FADE_COLORS)[number];

/** Black-lid reshuffle cadence for mosaic (ms). */
export const SITE_HERO_MOSAIC_LID_INTERVAL_MS = 4500;

/** Opacity dissolve duration for mosaic lids (ms). */
export const SITE_HERO_MOSAIC_LID_DISSOLVE_MS = 900;

/** How often mosaic cell photos are reshuffled (ms) — slower than lid cadence. */
export const SITE_HERO_MOSAIC_PHOTO_SHUFFLE_MS = 16000;

/** Crossfade when a mosaic cell swaps to another photo (ms). */
export const SITE_HERO_MOSAIC_PHOTO_CROSSFADE_MS = 900;

export type StorefrontSiteHeroSettings = {
  /**
   * Black wordmark splash before site content is shown.
   * Default on — can be turned off in Settings.
   */
  splashEnabled: boolean;
  /** Skip control on splash. */
  splashShowSkip: boolean;
  /** Minimum black-phase dwell before reveal (ms). */
  splashDwellMs: number;
  /** Splash and first-paint background (#rrggbb). */
  splashBackground: string;
  /** Splash text, wordmark and skip-control color (#rrggbb). */
  splashForeground: string;
  /** Wordmark animation preset (more presets later). */
  wordmarkPreset: SiteHeroWordmarkPreset;
  /** Still photo vs slideshow vs mosaic of catalog/disk images. */
  mediaMode: SiteHeroMediaMode;
  /**
   * Full-bleed photo under / after splash (single mode).
   * Empty → optional legacy fallback appearance.brandHeroImageUrl (migrated on normalize).
   */
  heroImageUrl: string | null;
  /**
   * Optional narrow-screen hero (square / portrait recommended) — single mode only.
   * When set, mobile uses this image; when empty, desktop photo scales by width and
   * the scroll-down cue is forced off on narrow viewports.
   */
  heroImageUrlMobile: string | null;
  /**
   * Shared photo pool for slideshow + mosaic.
   * Cap: SITE_HERO_URLS_STORAGE_MAX (UI caps by mode).
   */
  heroSlideUrls: string[];
  /** Time each slide stays visible before crossfade (ms). */
  slideshowIntervalMs: number;
  /** Mosaic desktop grid preset (ignored outside mosaic mode). */
  mosaicGrid: SiteHeroMosaicGrid;
  /**
   * Dark scrim over the photo for logo/title readability (0–70).
   * Default 40 ≈ former hardcoded bg-black/40. Unused in mosaic (lids provide dark).
   */
  photoOverlayOpacityPercent: number;
  /** Center overlay: tenant logo. */
  showLogo: boolean;
  /** Center overlay: brand / storefront name. */
  showTitle: boolean;
  /**
   * Brand-name size on Hero when showTitle is on (60–160, default 100).
   * Scales the title relative to the slot’s default type size.
   */
  titleSizePercent: number;
  /**
   * Fade-in the banner from a solid color after splash or on load.
   * Default off — enable in Settings → Hero.
   */
  overlayFadeInEnabled: boolean;
  /** Fade duration (ms). */
  overlayFadeInMs: number;
  /** Solid color the banner reveals from (black or white). */
  overlayFadeColor: SiteHeroOverlayFadeColor;
  /**
   * Vertical position of logo/title overlay on Hero (0–100).
   * 0 = near bottom, 50 = center (default), 100 = near top — same idea as catalog caption lift.
   */
  logoLiftPercent: number;
  /** Hero block min height as % of viewport (40–100). */
  heroMinHeightVh: number;
  /**
   * Animated mouse / scroll cue at the bottom of Hero.
   * Default on — hints that more content waits below the fold.
   */
  scrollHintEnabled: boolean;
};

export const DEFAULT_STOREFRONT_SITE_HERO: StorefrontSiteHeroSettings = {
  splashEnabled: true,
  splashShowSkip: true,
  splashDwellMs: 3200,
  splashBackground: '#000000',
  splashForeground: '#ffffff',
  wordmarkPreset: 'none',
  mediaMode: 'single',
  heroImageUrl: null,
  heroImageUrlMobile: null,
  heroSlideUrls: [],
  slideshowIntervalMs: 5000,
  mosaicGrid: '5x3',
  photoOverlayOpacityPercent: 40,
  showLogo: true,
  showTitle: true,
  titleSizePercent: 100,
  overlayFadeInEnabled: false,
  overlayFadeInMs: 2000,
  overlayFadeColor: 'black',
  logoLiftPercent: 50,
  heroMinHeightVh: 70,
  scrollHintEnabled: true,
};

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function asBool(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return fallback;
}

function asString(raw: unknown, fallback = ''): string {
  if (typeof raw !== 'string') return fallback;
  return raw.trim();
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Safe #rrggbb for splash / boot paint (rejects urls and leftover junk). */
export function normalizeSplashBackground(raw: unknown, fallback = '#000000'): string {
  const s = asString(raw);
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

export function splashForegroundForBackground(hex: string): '#ffffff' | '#111111' {
  const n = normalizeSplashBackground(hex);
  const r = parseInt(n.slice(1, 3), 16);
  const g = parseInt(n.slice(3, 5), 16);
  const b = parseInt(n.slice(5, 7), 16);
  const y = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return y > 0.55 ? '#111111' : '#ffffff';
}

function normalizeSlideUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const url = asString(item);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
    if (out.length >= SITE_HERO_URLS_STORAGE_MAX) break;
  }
  return out;
}

/** UI / picker cap for the active media mode. */
export function siteHeroSlidesCap(mode: SiteHeroMediaMode): number {
  if (mode === 'mosaic') return SITE_HERO_MOSAIC_SLIDES_MAX;
  if (mode === 'slideshow') return SITE_HERO_SLIDES_MAX;
  return 1;
}

export function siteHeroMosaicGridDims(grid: SiteHeroMosaicGrid): { cols: number; rows: number } {
  const [colsRaw, rowsRaw] = grid.split('x');
  const cols = Number(colsRaw);
  const rows = Number(rowsRaw);
  if (!Number.isFinite(cols) || !Number.isFinite(rows) || cols < 1 || rows < 1) {
    return { cols: 5, rows: 3 };
  }
  return { cols, rows };
}

/** Black lids per row (~3). All columns are fair game, including center. */
export function siteHeroMosaicLidCount(cols: number): number {
  return Math.min(3, Math.max(0, cols - 1));
}

function mosaicAllCols(cols: number): number[] {
  return Array.from({ length: cols }, (_, c) => c);
}

function mosaicSameColSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((c) => sb.has(c));
}

/**
 * Next columns for each lid in one row (stable lid indices 0..n-1).
 * - Every lid re-rolls alone until its column ≠ its previous column.
 * - Lids cannot share a cell.
 * - Rejects pure swaps / same covered set (would look identical on screen).
 */
export function siteHeroMosaicNextLidCols(
  cols: number,
  previousLids: number[] | null | undefined,
  rng: () => number = Math.random,
): number[] {
  const n = siteHeroMosaicLidCount(cols);
  const candidates = mosaicAllCols(cols);
  const prev =
    previousLids && previousLids.length === n ? previousLids : null;

  const rollOnce = (): number[] => {
    const next: number[] = [];
    for (let lid = 0; lid < n; lid++) {
      const prevCol = prev ? prev[lid] : undefined;
      let chosen: number | null = null;

      for (let attempt = 0; attempt < 64; attempt++) {
        // Never keep this lid on its previous cell when another free cell exists.
        const pool = candidates.filter(
          (c) => !next.includes(c) && c !== prevCol,
        );
        if (pool.length > 0) {
          chosen = pool[Math.floor(rng() * pool.length)]!;
          break;
        }
        // Only previous cell left for this lid.
        const stuck = candidates.filter((c) => !next.includes(c));
        if (stuck.length > 0) {
          chosen = stuck[0]!;
          break;
        }
      }

      if (chosen == null) {
        const pool = candidates.filter(
          (c) => !next.includes(c) && c !== prevCol,
        );
        const fallback = candidates.filter((c) => !next.includes(c));
        chosen = pool[0] ?? fallback[0] ?? candidates[0] ?? 0;
      }
      next.push(chosen);
    }
    return next;
  };

  // First layout — any distinct columns.
  if (!prev) return rollOnce();

  // Re-roll the whole row if the covered set did not change (e.g. two lids swapped).
  for (let round = 0; round < 32; round++) {
    const next = rollOnce();
    const eachMoved = next.every((c, i) => c !== prev[i]);
    if (eachMoved && !mosaicSameColSet(next, prev)) return next;
  }
  // Last resort: still prefer a different set even if one lid had to stay.
  for (let round = 0; round < 16; round++) {
    const next = rollOnce();
    if (!mosaicSameColSet(next, prev)) return next;
  }
  return rollOnce();
}

/** Initial lid columns for every row (stable identities). */
export function siteHeroMosaicInitialLidRows(
  cols: number,
  rows: number,
  rng: () => number = Math.random,
): number[][] {
  return Array.from({ length: rows }, () => siteHeroMosaicNextLidCols(cols, null, rng));
}

/** Advance each row: each lid moves off its previous cell when possible. */
export function siteHeroMosaicNextLidRows(
  cols: number,
  rows: number,
  previousRows: number[][] | null | undefined,
  rng: () => number = Math.random,
): number[][] {
  const n = siteHeroMosaicLidCount(cols);
  return Array.from({ length: rows }, (_, r) => {
    const prev =
      previousRows
      && previousRows[r]
      && previousRows[r]!.length === n
        ? previousRows[r]!
        : null;
    return siteHeroMosaicNextLidCols(cols, prev, rng);
  });
}

/** Opaque mask from stable lid-column rows (true = black lid). */
export function siteHeroMosaicMaskFromLidRows(
  cols: number,
  rows: number,
  lidRows: number[][],
): boolean[] {
  const mask = new Array<boolean>(cols * rows).fill(false);
  for (let r = 0; r < rows; r++) {
    const lids = lidRows[r] ?? [];
    for (const c of lids) {
      if (c >= 0 && c < cols) mask[r * cols + c] = true;
    }
  }
  return mask;
}

/** Fill mosaic cells by cycling the photo pool (before shuffle). */
export function siteHeroMosaicBuildCellUrls(urls: string[], cellCount: number): string[] {
  if (urls.length === 0 || cellCount < 1) return [];
  return Array.from({ length: cellCount }, (_, i) => urls[i % urls.length]!);
}

function mosaicCellUrlListsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Shuffle photos across mosaic cells. Retries when the layout matches `previous`
 * so a tick is never a no-op when more than one distinct URL exists.
 * Prefers layouts where each cell that can change does change (≠ previous[i]).
 */
export function siteHeroMosaicShuffleCellUrls(
  urls: string[],
  cellCount: number,
  previous: string[] | null | undefined,
  rng: () => number = Math.random,
): string[] {
  const distinct = new Set(urls.filter(Boolean)).size;
  const roll = (): string[] => {
    const next = siteHeroMosaicBuildCellUrls(urls, cellCount);
    for (let i = next.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = next[i]!;
      next[i] = next[j]!;
      next[j] = tmp;
    }
    return next;
  };

  let next = roll();
  if (!previous || previous.length !== next.length || distinct < 2) return next;

  const scoreMoved = (layout: string[]) =>
    layout.reduce((n, url, i) => n + (url !== previous[i] ? 1 : 0), 0);

  let best = next;
  let bestMoved = scoreMoved(next);
  for (let attempt = 0; attempt < 48; attempt++) {
    if (!mosaicCellUrlListsEqual(next, previous) && bestMoved === cellCount) {
      return next;
    }
    if (!mosaicCellUrlListsEqual(next, previous) && scoreMoved(next) > bestMoved) {
      best = next;
      bestMoved = scoreMoved(next);
    }
    next = roll();
  }
  if (!mosaicCellUrlListsEqual(best, previous)) return best;
  return roll();
}

/**
 * Fluid title size for Hero brand name (showTitle).
 * percent 100 ≈ former Tailwind xl / lg / md defaults.
 */
export function siteHeroTitleFontSizeStyle(
  scale: 'xl' | 'lg' | 'md' | string,
  titleSizePercent: number,
): { fontSize: string } {
  const p = Math.min(160, Math.max(60, titleSizePercent)) / 100;
  if (scale === 'xl') {
    return { fontSize: `clamp(${1.5 * p}rem, ${5 * p}vw, ${3 * p}rem)` };
  }
  if (scale === 'lg') {
    return { fontSize: `clamp(${1.25 * p}rem, ${4 * p}vw, ${2.25 * p}rem)` };
  }
  return { fontSize: `clamp(${1.1 * p}rem, ${3.5 * p}vw, ${1.875 * p}rem)` };
}

export function normalizeStorefrontSiteHero(raw: unknown): StorefrontSiteHeroSettings {
  const source = asRecord(raw);
  const d = DEFAULT_STOREFRONT_SITE_HERO;
  const presetRaw = asString(source.wordmarkPreset ?? source.wordmark_preset).toLowerCase();
  const wordmarkPreset = (SITE_HERO_WORDMARK_PRESETS as readonly string[]).includes(presetRaw)
    ? (presetRaw as SiteHeroWordmarkPreset)
    : d.wordmarkPreset;
  const imageRaw = asString(source.heroImageUrl ?? source.hero_image_url);
  const imageMobileRaw = asString(
    source.heroImageUrlMobile ?? source.hero_image_url_mobile,
  );
  const modeRaw = asString(source.mediaMode ?? source.media_mode).toLowerCase();
  const mediaMode = (SITE_HERO_MEDIA_MODES as readonly string[]).includes(modeRaw)
    ? (modeRaw as SiteHeroMediaMode)
    : d.mediaMode;
  let heroSlideUrls = normalizeSlideUrls(source.heroSlideUrls ?? source.hero_slide_urls);
  // Seed multi-photo modes from legacy single photo when switching / importing.
  if (
    (mediaMode === 'slideshow' || mediaMode === 'mosaic')
    && heroSlideUrls.length === 0
    && imageRaw
  ) {
    heroSlideUrls = [imageRaw];
  }
  const gridRaw = asString(source.mosaicGrid ?? source.mosaic_grid).toLowerCase();
  const mosaicGrid = (SITE_HERO_MOSAIC_GRIDS as readonly string[]).includes(gridRaw)
    ? (gridRaw as SiteHeroMosaicGrid)
    : d.mosaicGrid;
  const fadeColorRaw = asString(
    source.overlayFadeColor ?? source.overlay_fade_color,
  ).toLowerCase();
  const overlayFadeColor = (SITE_HERO_OVERLAY_FADE_COLORS as readonly string[]).includes(fadeColorRaw)
    ? (fadeColorRaw as SiteHeroOverlayFadeColor)
    : d.overlayFadeColor;
  const splashBackground = normalizeSplashBackground(
    source.splashBackground ?? source.splash_background,
    d.splashBackground,
  );
  const splashForegroundRaw = source.splashForeground ?? source.splash_foreground;
  return {
    splashEnabled: asBool(source.splashEnabled ?? source.splash_enabled, d.splashEnabled),
    splashShowSkip: asBool(source.splashShowSkip ?? source.splash_show_skip, d.splashShowSkip),
    splashDwellMs: clampInt(
      source.splashDwellMs ?? source.splash_dwell_ms,
      800,
      8000,
      d.splashDwellMs,
    ),
    splashBackground,
    splashForeground: splashForegroundRaw == null
      ? splashForegroundForBackground(splashBackground)
      : normalizeSplashBackground(splashForegroundRaw, d.splashForeground),
    wordmarkPreset,
    mediaMode,
    heroImageUrl: imageRaw || null,
    heroImageUrlMobile: imageMobileRaw || null,
    heroSlideUrls,
    slideshowIntervalMs: clampInt(
      source.slideshowIntervalMs ?? source.slideshow_interval_ms,
      2500,
      12000,
      d.slideshowIntervalMs,
    ),
    mosaicGrid,
    photoOverlayOpacityPercent: clampInt(
      source.photoOverlayOpacityPercent ?? source.photo_overlay_opacity_percent,
      0,
      70,
      d.photoOverlayOpacityPercent,
    ),
    showLogo: asBool(source.showLogo ?? source.show_logo, d.showLogo),
    showTitle: asBool(source.showTitle ?? source.show_title, d.showTitle),
    titleSizePercent: clampInt(
      source.titleSizePercent ?? source.title_size_percent,
      60,
      160,
      d.titleSizePercent,
    ),
    overlayFadeInEnabled: asBool(
      source.overlayFadeInEnabled ?? source.overlay_fade_in_enabled,
      d.overlayFadeInEnabled,
    ),
    overlayFadeInMs: clampInt(
      source.overlayFadeInMs ?? source.overlay_fade_in_ms,
      500,
      10000,
      d.overlayFadeInMs,
    ),
    overlayFadeColor,
    logoLiftPercent: clampInt(
      source.logoLiftPercent ?? source.logo_lift_percent,
      0,
      100,
      d.logoLiftPercent,
    ),
    heroMinHeightVh: clampInt(
      source.heroMinHeightVh ?? source.hero_min_height_vh,
      40,
      100,
      d.heroMinHeightVh,
    ),
    scrollHintEnabled: asBool(
      source.scrollHintEnabled ?? source.scroll_hint_enabled,
      d.scrollHintEnabled,
    ),
  };
}

/**
 * Absolute position for Hero logo/title stack.
 * Soft edges keep the block inside the frame (≈12%…88% of height).
 */
export function siteHeroLogoAnchorStyle(liftPercent: number): {
  top: string;
  left: string;
  transform: string;
} {
  const lift = Math.min(100, Math.max(0, liftPercent));
  // 0 → near bottom (88%), 50 → center (50%), 100 → near top (12%).
  const softTop = 88 - (lift / 100) * 76;
  return {
    top: `${softTop}%`,
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
}

/** Resolve hero photo: siteHero first, then appearance brand hero. */
export function resolveSiteHeroImageUrl(
  siteHero: StorefrontSiteHeroSettings,
  brandHeroImageUrl: string | null | undefined,
): string | null {
  if (siteHero.mediaMode === 'slideshow' || siteHero.mediaMode === 'mosaic') {
    const first = siteHero.heroSlideUrls[0];
    if (first) return first;
  }
  if (siteHero.heroImageUrl) return siteHero.heroImageUrl;
  const fallback = typeof brandHeroImageUrl === 'string' ? brandHeroImageUrl.trim() : '';
  return fallback || null;
}

/** Desktop/shared slide list for the live banner (1+ URLs). */
export function resolveSiteHeroSlideUrls(
  siteHero: StorefrontSiteHeroSettings,
  brandHeroImageUrl?: string | null,
): string[] {
  if (siteHero.mediaMode === 'slideshow' || siteHero.mediaMode === 'mosaic') {
    if (siteHero.heroSlideUrls.length > 0) {
      const cap = siteHeroSlidesCap(siteHero.mediaMode);
      return siteHero.heroSlideUrls.slice(0, cap);
    }
  }
  const single = resolveSiteHeroImageUrl(
    { ...siteHero, mediaMode: 'single' },
    brandHeroImageUrl,
  );
  return single ? [single] : [];
}

/** Dedicated mobile hero — no legacy fallback (owner must upload explicitly). */
export function resolveSiteHeroMobileImageUrl(
  siteHero: StorefrontSiteHeroSettings,
): string | null {
  if (siteHero.mediaMode === 'slideshow' || siteHero.mediaMode === 'mosaic') return null;
  const url = typeof siteHero.heroImageUrlMobile === 'string'
    ? siteHero.heroImageUrlMobile.trim()
    : '';
  return url || null;
}

/**
 * Whether the Hero banner has anything to show (photo and/or logo/title overlay).
 * When false the live site collapses the Hero block to zero height.
 */
export function siteHeroBannerVisible(
  siteHero: StorefrontSiteHeroSettings,
  brandHeroImageUrl?: string | null,
): boolean {
  if (siteHero.showLogo || siteHero.showTitle) return true;
  return resolveSiteHeroSlideUrls(siteHero, brandHeroImageUrl).length > 0
    || Boolean(resolveSiteHeroMobileImageUrl(siteHero));
}
